import { Extension } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Plugin } from '@tiptap/pm/state';
import type { EditorView, NodeView, ViewMutationRecord } from '@tiptap/pm/view';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import {
  ResizeState,
  TableMap,
  cellAround,
  columnResizingPluginKey,
  tableNodeTypes,
} from '@tiptap/pm/tables';
import { TableView, updateColumns } from '@tiptap/extension-table';
import { HIDDEN_ROW_PRICES_CLASS } from './investmentHiddenHours';

const CELL_MIN_WIDTH = 72;
// Breedte van de grijpzone rond een kolomrand, in schermpixels.
const EDGE_ZONE = 6;
const RESIZING_CLASS = 'is-column-resizing';
const PREVIEW_CLASS = 'is-column-preview';
const PREVIEW_STYLE_ID = 'investment-column-resize-preview';

type DragPosition = {
  startX: number;
  startWidth: number;
  maxWidth?: number;
};

/** Per rasterkolom de vastgelegde breedte (of null als de kolom meebeweegt). */
function storedColumnWidths(row: PMNode): (number | null)[] {
  const widths: (number | null)[] = [];

  row.forEach((cell) => {
    const colspan = Number(cell.attrs.colspan) || 1;
    const colwidth = cell.attrs.colwidth as number[] | null;
    for (let spanIndex = 0; spanIndex < colspan; spanIndex += 1) {
      widths.push(colwidth?.[spanIndex] || null);
    }
  });

  return widths;
}

/**
 * Zelfde vorm als de tableRow-nodeview van @tiptap-pro/extension-pages
 * (`<breedte>px` of `1fr`). Wijkt de preview daarvan af, dan verspringt de
 * tabel op het moment dat je de muis loslaat.
 */
function gridColumnsForRow(row: PMNode, overrideColumn = -1, overrideWidth = 0): string {
  const widths = storedColumnWidths(row);
  return widths
    .map((stored, index) => {
      const width = index === overrideColumn ? overrideWidth : stored;
      return width ? `${Math.round(width)}px` : '1fr';
    })
    .join(' ');
}

/**
 * Ruimte die de andere kolommen minimaal nodig hebben; de gesleepte kolom mag
 * nooit breder worden dan wat daarna overblijft, anders loopt de tabel buiten
 * de pagina en "springt" de layout tijdens het slepen.
 */
export function columnMaxWidth(
  widths: (number | null)[],
  column: number,
  availableWidth: number
): number {
  let reserved = 0;

  widths.forEach((stored, index) => {
    if (index === column) return;
    reserved += stored ?? CELL_MIN_WIDTH;
  });

  return Math.max(CELL_MIN_WIDTH, availableWidth - reserved);
}

/**
 * Fallback-rijweergave. In de praktijk wint de tableRow-nodeview van
 * @tiptap-pro/extension-pages (extensie-nodeviews gaan voor die van plugins);
 * deze klasse houdt de rasterlayout intact als die extensie er niet is.
 */
class InvestmentTableRowView implements NodeView {
  node: PMNode;
  dom: HTMLTableRowElement;
  contentDOM: HTMLTableRowElement;

  constructor(node: PMNode) {
    this.node = node;
    this.dom = document.createElement('tr');
    this.contentDOM = this.dom;
    this.applyColumns();
  }

  private applyColumns() {
    this.dom.style.display = 'grid';
    this.dom.style.width = '100%';
    this.dom.style.alignItems = 'stretch';
    this.dom.style.gridTemplateColumns = gridColumnsForRow(this.node);
  }

  update(node: PMNode) {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.applyColumns();
    return true;
  }

  // Een style-mutatie op de rij is layout, geen content: zonder dit bouwt
  // ProseMirror de hele rij opnieuw op.
  ignoreMutation(mutation: ViewMutationRecord) {
    return mutation.type === 'attributes' && mutation.attributeName === 'style';
  }
}

function readEditorZoom(view: EditorView): number {
  const host = view.dom.closest<HTMLElement>('[data-editor-zoom]');
  const zoom = Number(host?.dataset.editorZoom);
  return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
}

function domCellAround(target: EventTarget | null): HTMLTableCellElement | null {
  let element = target instanceof HTMLElement ? target : null;

  while (element && element.nodeName !== 'TD' && element.nodeName !== 'TH') {
    if (element.classList.contains('ProseMirror')) return null;
    element = element.parentElement;
  }

  return element as HTMLTableCellElement | null;
}

/**
 * Positie van een cel rechtstreeks uit de DOM. Betrouwbaarder dan posAtCoords
 * met een horizontale offset: die valt vlak bij een celrand regelmatig in de
 * verkeerde cel of buiten de tabel, waardoor de greep wegviel.
 */
function cellPosFromDom(view: EditorView, cellDom: HTMLElement): number {
  let pos: number;
  try {
    pos = view.posAtDOM(cellDom, 0);
  } catch {
    return -1;
  }
  if (pos < 0) return -1;

  const $cell = cellAround(view.state.doc.resolve(pos));
  return $cell ? $cell.pos : -1;
}

type ColumnInfo = {
  table: PMNode;
  cell: PMNode;
  map: TableMap;
  start: number;
  column: number;
};

function columnInfo(view: EditorView, cellPos: number): ColumnInfo | null {
  if (cellPos < 0 || cellPos > view.state.doc.content.size) return null;
  const $cell = view.state.doc.resolve(cellPos);
  const table = $cell.node(-1);
  const cell = $cell.nodeAfter;
  if (!table || !cell) return null;

  const map = TableMap.get(table);
  const start = $cell.start(-1);
  const column = map.colCount($cell.pos - start) + (Number(cell.attrs.colspan) || 1) - 1;
  return { table, cell, map, start, column };
}

function updateHandle(view: EditorView, value: number) {
  view.dispatch(view.state.tr.setMeta(columnResizingPluginKey, { setHandle: value }));
}

function cellDomAt(view: EditorView, cellPos: number): HTMLElement | null {
  const dom = view.nodeDOM(cellPos);
  return dom instanceof HTMLElement ? dom : null;
}

/**
 * Werkelijk gerenderde breedte van de kolom. We meten de DOM in plaats van de
 * opgeslagen attr-waarde, zodat de kolom niet verspringt op het moment dat je
 * begint te slepen.
 */
function currentColumnWidth(view: EditorView, cellPos: number, cell: PMNode, zoom: number): number {
  const colspan = Number(cell.attrs.colspan) || 1;
  const colwidth = cell.attrs.colwidth as number[] | null;
  const cellDom = cellDomAt(view, cellPos);
  const measured = cellDom ? cellDom.getBoundingClientRect().width / zoom : 0;

  if (measured > 0) {
    let width = measured;
    let unsetParts = colspan;
    for (let index = 0; index < colspan - 1; index += 1) {
      const stored = colwidth?.[index];
      if (!stored) continue;
      width -= stored;
      unsetParts -= 1;
    }
    return Math.max(CELL_MIN_WIDTH, width / Math.max(1, unsetParts));
  }

  return Math.max(CELL_MIN_WIDTH, colwidth?.[colspan - 1] || CELL_MIN_WIDTH);
}

export function columnWidthFromPointer(drag: DragPosition, clientX: number, zoom: number): number {
  const layoutDelta = (clientX - drag.startX) / zoom;
  const width = Math.max(CELL_MIN_WIDTH, drag.startWidth + layoutDelta);
  return drag.maxWidth ? Math.min(width, drag.maxWidth) : width;
}

function tableDomAtCell(view: EditorView, cellPos: number): HTMLTableElement | null {
  let dom: Node | null = view.domAtPos(view.state.doc.resolve(cellPos).start(-1)).node;
  while (dom && dom.nodeName !== 'TABLE') dom = dom.parentNode;
  return dom instanceof HTMLTableElement ? dom : null;
}

/** Beschikbare layoutbreedte voor de tabel (dus zonder editorzoom). */
function availableTableWidth(view: EditorView, cellPos: number, zoom: number): number {
  const tableDom = tableDomAtCell(view, cellPos);
  const container = tableDom?.parentElement ?? tableDom;
  const width = container ? container.getBoundingClientRect().width / zoom : 0;
  return width > 0 ? width : Number.POSITIVE_INFINITY;
}

function previewStyleElement(doc: Document): HTMLStyleElement {
  const existing = doc.getElementById(PREVIEW_STYLE_ID);
  if (existing instanceof HTMLStyleElement) return existing;

  const element = doc.createElement('style');
  element.id = PREVIEW_STYLE_ID;
  doc.head.appendChild(element);
  return element;
}

/**
 * Toont de nieuwe kolombreedte tijdens het slepen.
 *
 * Bewust via een stylesheet-regel en niet via inline styles op de rijen:
 * ProseMirror ziet zo'n style-mutatie als een contentwijziging, en omdat de
 * rij-nodeview van de Pages-extensie geen update ondersteunt werd elke rij
 * daarna opnieuw opgebouwd. De breedte sprong dan meteen terug en het slepen
 * voelde hakkelig. Een klasse op het table-element is wel veilig: TableView
 * negeert mutaties buiten de tbody.
 */
function displayColumnWidth(view: EditorView, cellPos: number, width: number) {
  const info = columnInfo(view, cellPos);
  const tableDom = tableDomAtCell(view, cellPos);
  if (!info || !tableDom || !info.table.firstChild) return;

  const template = gridColumnsForRow(info.table.firstChild, info.column, width);
  tableDom.classList.add(PREVIEW_CLASS);

  const style = previewStyleElement(tableDom.ownerDocument);
  const rule = `.${PREVIEW_CLASS} > tbody > tr { grid-template-columns: ${template} !important; }`;
  if (style.textContent !== rule) style.textContent = rule;

  const colgroup = tableDom.querySelector(':scope > colgroup');
  if (colgroup instanceof HTMLTableColElement) {
    updateColumns(info.table, colgroup, tableDom, CELL_MIN_WIDTH, info.column, width);
  }
}

function clearColumnWidthPreview(view: EditorView) {
  const doc = view.dom.ownerDocument;
  doc
    .querySelectorAll(`.${PREVIEW_CLASS}`)
    .forEach((table) => table.classList.remove(PREVIEW_CLASS));
  const style = doc.getElementById(PREVIEW_STYLE_ID);
  if (style) style.textContent = '';
}

function updateColumnWidth(view: EditorView, cellPos: number, width: number | null) {
  const info = columnInfo(view, cellPos);
  if (!info) return;
  const { table, map, start, column } = info;
  const transaction = view.state.tr;

  for (let row = 0; row < map.height; row += 1) {
    const mapIndex = row * map.width + column;
    if (row && map.map[mapIndex] === map.map[mapIndex - map.width]) continue;

    const relativePos = map.map[mapIndex];
    const cell = table.nodeAt(relativePos);
    if (!cell) continue;

    const attrs = cell.attrs;
    const colspan = Number(attrs.colspan) || 1;
    const widthIndex = colspan === 1 ? 0 : column - map.colCount(relativePos);
    const nextWidth = width === null ? 0 : Math.round(width);
    if ((attrs.colwidth?.[widthIndex] ?? 0) === nextWidth) continue;

    const colwidth = attrs.colwidth ? [...attrs.colwidth] : Array<number>(colspan).fill(0);
    colwidth[widthIndex] = nextWidth;
    const hasWidth = colwidth.some((value) => value > 0);
    transaction.setNodeMarkup(start + relativePos, undefined, {
      ...attrs,
      colwidth: hasWidth ? colwidth : null,
    });
  }

  if (transaction.docChanged) view.dispatch(transaction);
}

/** Cellen van de kolom die op dit moment gesleept wordt. */
function draggingCellPositions(viewState: EditorView['state'], cellPos: number): Set<number> {
  const positions = new Set<number>();
  if (cellPos < 0 || cellPos > viewState.doc.content.size) return positions;

  const $cell = viewState.doc.resolve(cellPos);
  const table = $cell.node(-1);
  const activeCell = $cell.nodeAfter;
  if (!table || !activeCell) return positions;

  const map = TableMap.get(table);
  const start = $cell.start(-1);
  const col = map.colCount($cell.pos - start) + (Number(activeCell.attrs.colspan) || 1) - 1;
  for (let row = 0; row < map.height; row += 1) {
    positions.add(start + map.map[col + row * map.width]);
  }
  return positions;
}

/**
 * Grepen op elke kolomrand, altijd aanwezig in de DOM.
 *
 * Ze stonden er eerder pas zodra de muis vlak bij een rand kwam. Die wissel in de
 * decoratieset liet ProseMirror de rijen opnieuw opbouwen, en omdat een herbouwde
 * rij als raster wordt gerenderd sprong de hele tabel een paar pixels — precies op
 * het moment dat je de greep wilde pakken. Nu verandert er bij hover niets aan de
 * DOM; alleen de CSS laat de greep oplichten.
 */
function handleDecorations(
  viewState: EditorView['state'],
  draggingCellPos: number
): DecorationSet {
  const decorations: Decoration[] = [];
  const draggingCells =
    draggingCellPos > -1 ? draggingCellPositions(viewState, draggingCellPos) : new Set<number>();

  viewState.doc.descendants((node, pos) => {
    if (node.type.name !== 'investmentGroupNode') return true;

    node.forEach((child, offset) => {
      if (child.type.name !== 'table') return;

      const tablePos = pos + 1 + offset;
      const start = tablePos + 1;
      const map = TableMap.get(child);

      for (let col = 0; col < map.width; col += 1) {
        for (let row = 0; row < map.height; row += 1) {
          const index = col + row * map.width;
          const endsCell = col === map.width - 1 || map.map[index] !== map.map[index + 1];
          const startsCell = row === 0 || map.map[index] !== map.map[index - map.width];
          if (!endsCell || !startsCell) continue;

          const relativePos = map.map[index];
          const cell = child.nodeAt(relativePos);
          if (!cell) continue;

          const absolutePos = start + relativePos;
          if (draggingCells.has(absolutePos)) {
            decorations.push(
              Decoration.node(absolutePos, absolutePos + cell.nodeSize, {
                class: 'column-resize-dragging',
              })
            );
          }

          // Een stabiele key voorkomt dat ProseMirror de greep bij elke transactie
          // opnieuw opbouwt; dat liet de handgreep flikkeren en onderbrak het slepen.
          decorations.push(
            Decoration.widget(
              absolutePos + cell.nodeSize - 1,
              () => {
                const handle = document.createElement('div');
                handle.className = 'column-resize-handle';
                handle.contentEditable = 'false';
                return handle;
              },
              { key: `column-resize-${tablePos}-${col}-${row}`, side: 1, ignoreSelection: true }
            )
          );
        }
      }
    });

    return false;
  });

  return DecorationSet.create(viewState.doc, decorations);
}

/** Kolomrand onder de muis, of -1. */
function handleAtPointer(view: EditorView, event: MouseEvent): number {
  const cellDom = domCellAround(event.target);
  // Zonder bedragkolommen is er alleen de omschrijving, die de volle breedte vult.
  if (!cellDom || cellDom.closest(`table.${HIDDEN_ROW_PRICES_CLASS}`)) return -1;

  const { left, right } = cellDom.getBoundingClientRect();
  let targetDom: HTMLElement | null = null;

  if (right - event.clientX <= EDGE_ZONE) {
    targetDom = cellDom;
  } else if (event.clientX - left <= EDGE_ZONE) {
    const previous = cellDom.previousElementSibling;
    targetDom =
      previous instanceof HTMLElement && (previous.nodeName === 'TD' || previous.nodeName === 'TH')
        ? previous
        : null;
  }

  if (!targetDom) return -1;

  const cellPos = cellPosFromDom(view, targetDom);
  const info = columnInfo(view, cellPos);
  if (!info) return -1;

  // De laatste kolom vult de resterende ruimte en is dus niet sleepbaar.
  if (info.column === info.map.width - 1) return -1;
  return cellPos;
}

function createZoomAwareColumnResizePlugin() {
  const plugin = new Plugin<ResizeState>({
    key: columnResizingPluginKey,
    state: {
      init: (_, state) => {
        const nodeViews = plugin.spec.props?.nodeViews;
        if (nodeViews) {
          const nodeTypes = tableNodeTypes(state.schema);
          const tableName = nodeTypes.table.name;
          const rowName = nodeTypes.row.name;
          nodeViews[tableName] = (node) => new TableView(node, CELL_MIN_WIDTH);
          nodeViews[rowName] = (node) => new InvestmentTableRowView(node);
        }
        return new ResizeState(-1, false);
      },
      apply: (transaction, previous) => previous.apply(transaction),
    },
    props: {
      attributes: (state) =>
        (columnResizingPluginKey.getState(state)?.activeHandle ?? -1) > -1
          ? { class: 'resize-cursor' }
          : { class: '' },
      handleDOMEvents: {
        mousemove: (view, event) => {
          if (!view.editable) return false;
          const resizeState = columnResizingPluginKey.getState(view.state);
          if (!resizeState || resizeState.dragging) return false;

          const cell = handleAtPointer(view, event);
          if (cell !== resizeState.activeHandle) updateHandle(view, cell);
          return false;
        },
        mouseleave: (view) => {
          const resizeState = columnResizingPluginKey.getState(view.state);
          if (resizeState && resizeState.activeHandle > -1 && !resizeState.dragging) {
            updateHandle(view, -1);
          }
          return false;
        },
        dblclick: (view, event) => {
          if (!view.editable) return false;
          const cell = handleAtPointer(view, event);
          if (cell < 0) return false;

          // Dubbelklik op een rand zet de kolom terug naar automatisch verdelen.
          updateColumnWidth(view, cell, null);
          event.preventDefault();
          return true;
        },
        mousedown: (view, event) => {
          if (!view.editable || event.button !== 0) return false;
          const resizeState = columnResizingPluginKey.getState(view.state);
          if (!resizeState || resizeState.dragging) return false;

          const handlePos =
            resizeState.activeHandle > -1 ? resizeState.activeHandle : handleAtPointer(view, event);
          const info = columnInfo(view, handlePos);
          if (!info) return false;

          const zoom = readEditorZoom(view);
          const startWidth = currentColumnWidth(view, handlePos, info.cell, zoom);
          const widths = info.table.firstChild ? storedColumnWidths(info.table.firstChild) : [];
          const available = availableTableWidth(view, handlePos, zoom);
          const drag: DragPosition = {
            startX: event.clientX,
            startWidth,
            // Nooit smaller dan de huidige breedte: een bestaande (te brede)
            // kolom mag niet meteen terugspringen bij het aanklikken.
            maxWidth: Number.isFinite(available)
              ? Math.max(startWidth, columnMaxWidth(widths, info.column, available))
              : undefined,
          };

          const win = view.dom.ownerDocument.defaultView ?? window;
          let latestClientX = event.clientX;
          let animationFrame = 0;
          let finished = false;

          if (resizeState.activeHandle !== handlePos) updateHandle(view, handlePos);
          view.dispatch(
            view.state.tr.setMeta(columnResizingPluginKey, {
              setDragging: drag,
            })
          );
          view.dom.classList.add(RESIZING_CLASS);

          const renderFrame = () => {
            animationFrame = 0;
            const state = columnResizingPluginKey.getState(view.state);
            if (!state?.dragging) return;
            displayColumnWidth(
              view,
              state.activeHandle,
              columnWidthFromPointer(drag, latestClientX, zoom)
            );
          };

          const stop = () => {
            if (finished) return null;
            finished = true;
            win.removeEventListener('mouseup', finish);
            win.removeEventListener('mousemove', move);
            win.removeEventListener('keydown', cancel, true);
            if (animationFrame) win.cancelAnimationFrame(animationFrame);
            view.dom.classList.remove(RESIZING_CLASS);

            const state = columnResizingPluginKey.getState(view.state);
            if (!state?.dragging) return null;
            return state;
          };

          const finish = (finishEvent: MouseEvent) => {
            const state = stop();
            if (!state) return;
            const finalWidth = columnWidthFromPointer(drag, finishEvent.clientX, zoom);
            displayColumnWidth(view, state.activeHandle, finalWidth);
            // Eerst vastleggen (ProseMirror werkt de DOM synchroon bij), daarna
            // pas de preview weghalen: zo is er geen tussenframe met de oude breedte.
            updateColumnWidth(view, state.activeHandle, finalWidth);
            view.dispatch(view.state.tr.setMeta(columnResizingPluginKey, { setDragging: null }));
            clearColumnWidthPreview(view);
          };

          const cancel = (keyEvent: KeyboardEvent) => {
            if (keyEvent.key !== 'Escape') return;
            const state = stop();
            if (!state) return;
            keyEvent.preventDefault();
            view.dispatch(
              view.state.tr.setMeta(columnResizingPluginKey, { setDragging: null, setHandle: -1 })
            );
            clearColumnWidthPreview(view);
          };

          const move = (moveEvent: MouseEvent) => {
            if (moveEvent.buttons === 0 && moveEvent.which === 0) {
              finish(moveEvent);
              return;
            }

            latestClientX = moveEvent.clientX;
            if (!animationFrame) animationFrame = win.requestAnimationFrame(renderFrame);
          };

          displayColumnWidth(view, handlePos, startWidth);
          win.addEventListener('mouseup', finish);
          win.addEventListener('mousemove', move);
          win.addEventListener('keydown', cancel, true);
          event.preventDefault();
          return true;
        },
      },
      decorations: (state) => {
        const resizeState = columnResizingPluginKey.getState(state);
        // Altijd dezelfde set: hoveren mag de DOM niet veranderen.
        return handleDecorations(
          state,
          resizeState?.dragging ? resizeState.activeHandle : -1
        );
      },
      nodeViews: {},
    },
  });

  return plugin;
}

export const InvestmentColumnResize = Extension.create({
  name: 'investmentColumnResize',

  addProseMirrorPlugins() {
    return [createZoomAwareColumnResizePlugin()];
  },
});
